import React from 'react';
import * as Sentry from '@sentry/react';

// ── Crash containment for the routed page only ───────────────────────────────
// The app's single error boundary sits OUTSIDE BrowserRouter, wrapping
// AuthProvider and the router together. So a crash on one page replaced the
// entire application — shell, navigation and all — and the only way out was
// `window.location.href = '/'`, a full reload that discards every bit of app
// state the user had built up.
//
// This boundary sits INSIDE the router, around <Routes>. Two consequences:
//   • A page that throws takes down that page, not the whole app.
//   • It resets when the route changes, so navigating away recovers on its own
//     — no reload. That matters because a deterministic crash (a malformed
//     record, a browser API that always throws) otherwise reproduces on every
//     retry and leaves the user stuck.
//
// The outer boundary stays as the last resort for anything thrown above the
// router — provider setup, routing itself.
//
// Unlike the outermost ErrorBoundary, this one DOES use theme tokens. It only
// catches render errors inside a routed page, by which point the theme layer is
// loaded — and it deliberately sets no background of its own, so it inherits the
// page ground. That makes tokens the only correct choice: no single literal
// colour reaches 4.5:1 against both a near-white and a near-black ground, since
// the luminance bands the two requirements allow do not overlap. Literals here
// measured 2.97:1 in dark mode and 3.91:1 in light. The brand purple on the
// primary button is an intentional hardcode — white on #6f42c1 either way.
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    // Belt and braces. In the current tree this rarely fires: the boundary sits
    // inside <div key={location.pathname}>, so navigating changes that key and
    // React remounts this component with fresh state, which clears the error on
    // its own. This keeps the reset working if that key is ever removed —
    // without it, dropping the key would silently strand the user on the error
    // screen for the rest of the session.
    if (this.state.hasError && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    Sentry.captureException(error, {
      extra: { ...errorInfo, route: this.props.routeKey },
    });
    console.error('[RouteErrorBoundary]', this.props.routeKey, error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center',
          padding: '3rem 1.5rem', minHeight: '60vh',
        }}
      >
        <div style={{ fontSize: '44px', marginBottom: '14px' }}>🌙</div>
        <h4 style={{ fontWeight: 700, marginBottom: '8px', color: 'var(--text-main)' }}>
          This page hit a snag
        </h4>
        <p style={{ color: 'var(--text-muted2)', marginBottom: '20px', maxWidth: 420 }}>
          Something went wrong loading this page — your stories are safe. Try
          again, or head back to your dashboard.
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{
              padding: '10px 22px', background: '#6f42c1', color: '#fff',
              border: 'none', borderRadius: '50px', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/dashboard'; }}
            style={{
              padding: '10px 22px', background: 'transparent', color: '#6f42c1',
              border: '1px solid #6f42c1', borderRadius: '50px', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
